import { Link } from "@/lib/router-compat";
import { getRouteApi } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Search, Truck, Wrench, CarFront, Phone, Package } from "lucide-react";
import Layout from "@/components/layout/Layout";
import type { HeroSlide } from "@/components/ui/HeroSlider";
import { Suspense, useState, lazy, useEffect } from "react";
import LazySection from "@/components/ui/LazySection";
import { toWebP, STATIC_ASSETS, optimizedImg } from "@/lib/imageUtils";
import { Skeleton } from "@/components/ui/skeleton";
import DeferredMount from "@/components/ui/DeferredMount";
import InteractionGate from "@/components/ui/InteractionGate";
import { useHeroContent } from "@/hooks/useHeroContent";
import HeroButtons from "@/components/ui/HeroButtons";
import WidgetZone from "@/components/ui/WidgetZone";
import MobileHeroStrip from "@/components/home/MobileHeroStrip";

import MobilePhoneCTA from "@/components/home/MobilePhoneCTA";
import MobileSearchPanelGate from "@/components/home/MobileSearchPanelGate";
import HeroSlider from "@/components/ui/HeroSlider";
import PromoCarousel from "@/components/home/PromoCarousel";

const DealOfTheWeek = lazy(() => import("@/components/home/DealOfTheWeek"));
const RVExpertWizard = lazy(() => import("@/components/inventory/RVExpertWizard"));
const RVExpertBanner = lazy(() => import("@/components/inventory/RVExpertBanner"));
const HeroSearchPanel = lazy(() => import("@/components/home/HeroSearchPanel"));

const homeRouteApi = getRouteApi("/");
const FeaturedRVs = lazy(() => import("@/components/home/FeaturedRVs"));
const BrandLogos = lazy(() => import("@/components/home/BrandLogos"));

/** Measured rendered heights of the lazy sections (mobile 412px / desktop 1350px).
 *  Placeholders must reserve at least this much or the swap shifts everything
 *  below them (this was the homepage's entire CLS: 2 x 0.157). */
const DEAL_RESERVE = "min-h-[736px] md:min-h-[553px]";
const FEATURED_RESERVE = "min-h-[912px] md:min-h-[697px]";

const testimonials = [
  {
    text: "My husband and I bought our first travel trailer from Rex & Sons, and it was a great experience. Rex & Sons is a fine example of customer service and product expertise that's hard to find these days. Thanks for the great experience!",
    author: "Andrea",
  },
  {
    text: "Great place for all of your camping needs and prices for new and used campers. Stop in and see Dave he will do his best to keep you updated on your camping needs.",
    author: "James H Barnhill II",
  },
];

const homepageHero1366For = (desktopUrl?: string) => {
  if (!desktopUrl) return undefined;
  const normalized = desktopUrl.replace(
    /^https?:\/\/(?:www\.)?myrvpart\.com\/images\/splash-assets\//,
    "/images/splash-assets/"
  );
  return normalized.includes("/splash-assets/hero-1920.webp") ? STATIC_ASSETS.hero1366 : undefined;
};

const Index = () => {
  const [wizardOpen, setWizardOpen] = useState(false);

  // Server-resolved device (Sec-CH-UA-Mobile, UA fallback). Renders one hero
  // branch instead of both, then self-corrects after hydration if the served
  // hint disagrees with the real viewport (resize, DevTools device toggle,
  // Chromium in a desktop-width window on a "mobile" UA, etc.).
  const ssrDevice = homeRouteApi.useLoaderData().device;
  const [device, setDevice] = useState<"mobile" | "desktop" | null>(ssrDevice);
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const apply = () => setDevice(mql.matches ? "mobile" : "desktop");
    apply();
    mql.addEventListener("change", apply);
    return () => mql.removeEventListener("change", apply);
  }, []);
  const showMobile = device !== "desktop";
  const showDesktop = device !== "mobile";

  // Hash deep-link: `#rex` opens the AI wizard and scrolls to the Meet Rex banner.
  useEffect(() => {
    const handleHash = () => {
      if (typeof window === "undefined") return;
      if (window.location.hash !== "#rex") return;
      setWizardOpen(true);
      const el = document.getElementById("rex");
      if (el) {
        // Defer so layout settles before scrolling.
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  // CMS-managed homepage hero (page_content: page_slug='home', section_key='hero_slides').
  // The desktop <link rel="preload"> for the first slide is injected by the
  // early bootstrap script in index.html (same REST row + Render API URLs).
  // No hard-coded slide fallback is rendered.
  const { data: cmsHero, isLoading: heroLoading } = useHeroContent("home", "hero_slides");
  const cmsMapped: HeroSlide[] = (cmsHero?.slides ?? [])
    .filter((s) => s.image_desktop || s.image_mobile)
    .map((s) => ({
    image: s.image_desktop || s.image_mobile || STATIC_ASSETS.hero1920,
    image1366: homepageHero1366For(s.image_desktop),
    imageMobile: s.image_mobile || s.image_desktop,
    title: s.title,
    subtitle: s.subtitle,
    cta: s.buttons && s.buttons.length > 0 ? <HeroButtons buttons={s.buttons} /> : undefined,
    fit: s.fit,
    hideText: s.hide_text,
  }));
  const effectiveSlides: HeroSlide[] = cmsMapped.length > 0 || heroLoading ? cmsMapped : [];


  const heroSearchPanel = (
    <Suspense fallback={<div className="min-h-[430px] rounded-2xl bg-secondary/95" aria-hidden />}>
      <HeroSearchPanel />
    </Suspense>
  );

  return (
    <Layout>
      {wizardOpen && (
        <Suspense fallback={null}>
          <RVExpertWizard open={wizardOpen} onOpenChange={setWizardOpen} />
        </Suspense>
      )}

      {/* ── MOBILE-ONLY: Blue Compass intent-first stack ────────── */}
      {/* No min-h cushion — children (MobileHeroStrip 200,
          MobilePhoneCTA) reserve their own heights.
          The previous 100svh wrapper produced a ~0.020 CLS as lazy sections
          hydrated inside it. */}
      {/* main's paddingTop (var(--header-h)) already reserves the fixed-header
          zone, so the 200px MobileHeroStrip starts directly below the header.
          (A previous -1*var(--header-h) margin here assumed the static shell
          carried an in-flow spacer; the React MobileHeroShell has none, so the
          compensation pulled the strip up UNDER the header.) */}
      {showMobile && (
      <div className="md:hidden">
        {/* Solid scrim behind the fixed transparent header so the white
            hamburger keeps AA contrast over light hero imagery. */}
        <div
          aria-hidden
          className="fixed top-0 left-0 right-0 z-40 bg-secondary pointer-events-none"
          style={{ height: "var(--header-h, 78px)" }}
        />
        <MobileHeroStrip />
        <div className="px-3 pt-5 relative z-30">
          <MobileSearchPanelGate
            placeholder={
              <div
                className="min-h-[430px] rounded-2xl border border-border/40 bg-secondary/95 p-4 shadow-2xl"
                aria-hidden
              />
            }
          >
            {heroSearchPanel}
          </MobileSearchPanelGate>
        </div>
        <MobilePhoneCTA />
      </div>
      )}

      {/* ── DESKTOP-ONLY: original hero slider ──────────────────── */}
      {/* HeroSlider imported eagerly so the LCP <img> is in the initial DOM
          (Lighthouse was picking a lazy FeaturedRVs card as LCP otherwise).
          The aspect-ratio classes inside HeroSlider reserve the box so
          we don't get CLS from a late-mounting hero. */}
      {showDesktop && (
      <div className="hidden md:block">
        <HeroSlider slides={effectiveSlides} height="md:min-h-[600px]" mobileImages={false} />
      </div>
      )}

      <InteractionGate placeholder={null}>
        <Suspense fallback={null}>
          <WidgetZone zone="home_page_top" />
        </Suspense>
      </InteractionGate>

      {/* Route 66 strip + promo carousel render immediately (cheap, no JS-heavy
          deps) so they're visible without engagement. Only the RV Expert
          banner (lazy chunk + CMS query) stays behind the interaction gate. */}
      <div className="hidden md:block bg-secondary py-2 text-center h-10 sm:h-12">
          <Link to="/route-66-rv-network" className="inline-flex items-center gap-2 h-full">
          <img
            src={STATIC_ASSETS.route66_64}
            alt="Route 66 RV Network"
            width={40}
            height={40}
            className="h-[24px] w-[24px] sm:h-[32px] sm:w-[32px] object-contain"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              const img = e.currentTarget;
              if (!img.dataset['retried']) {
                img.dataset['retried'] = "1";
                img.src = img.src.split("?")[0] + "?t=" + Date.now();
              }
            }}
          />
          <span className="font-heading text-xs uppercase tracking-wider text-secondary-foreground/70 sm:text-sm">
            Member of Route 66 RV Network
          </span>
          </Link>
      </div>

      {/* Desktop search panel sits directly below the Route 66 strip */}
      {showDesktop && (
        <div className="hidden md:block">
          <div className="container relative z-30 py-6">{heroSearchPanel}</div>
        </div>
      )}

      {/* CMS promo banner carousel (Admin → Pages → Promo Carousel) */}
      <PromoCarousel slug="home" />

      <InteractionGate placeholder={<div className="hidden min-h-[278px] md:block" aria-hidden />}>
        <div className="hidden md:block py-4 md:py-12">
          <Suspense fallback={<div className="min-h-[260px]" />}>
            <RVExpertBanner onStart={() => setWizardOpen(true)} />
          </Suspense>
        </div>
      </InteractionGate>

      <LazySection
        rootMargin="200px 0px"
        className={`bg-secondary ${DEAL_RESERVE}`}
        fallback={
          <div className={`py-12 bg-secondary ${DEAL_RESERVE}`}>
            <div className="container">
              <Skeleton className="h-10 w-64 mx-auto mb-8" />
              <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border bg-card shadow-xl">
                <div className="grid md:grid-cols-2">
                  <Skeleton className="aspect-[4/3]" />
                  <div className="p-6 md:p-8 space-y-4">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-40" />
                    <Skeleton className="h-12 w-44 rounded-md" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      >
        {/* A null Suspense fallback collapsed the reserved box to 0 height
            while the chunk loaded — reserve the same height here. */}
        <Suspense fallback={<div className={`py-12 bg-secondary ${DEAL_RESERVE}`} aria-hidden />}>
          <DealOfTheWeek />
        </Suspense>
      </LazySection>

      <InteractionGate placeholder={null}>
        <Suspense fallback={null}>
          <WidgetZone zone="home_page_before_Units" />
        </Suspense>
      </InteractionGate>


      <LazySection
        rootMargin="300px 0px"
        className={FEATURED_RESERVE}
        fallback={<div className={`py-16 ${FEATURED_RESERVE}`} />}
      >
        <Suspense
          fallback={
            <div className={`py-16 ${FEATURED_RESERVE}`}>
              <div className="container">
                <Skeleton className="h-10 w-64 mx-auto mb-3" />
                <Skeleton className="h-5 w-80 mx-auto mb-10" />
                <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border bg-card shadow-xl">
                  <div className="grid md:grid-cols-2">
                    <Skeleton className="aspect-[4/3]" />
                    <div className="p-6 md:p-8 space-y-4">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-8 w-48" />
                      <Skeleton className="h-10 w-40" />
                      <Skeleton className="h-12 w-44 rounded-md" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        >
          <FeaturedRVs />
        </Suspense>
      </LazySection>

      {/* Measured live: 2184px mobile / 696px desktop. */}
      <InteractionGate placeholder={<div className="min-h-[2184px] bg-muted md:min-h-[696px]" aria-hidden />}>
      <section className="bg-muted py-20">
        <div className="container">
          <div className="text-center">
            <h2 className="font-heading text-3xl font-bold uppercase text-foreground md:text-4xl">
              What We <span className="text-primary">Offer</span>
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
              From finding your dream RV to keeping it road-ready, we're here for every mile.
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "RV Sales",
                desc: "Browse our full inventory of new and pre-owned Class A, B, C motorhomes, travel trailers, fifth wheels, and more.",
                href: "/rv-search",
                icon: Truck,
                img: STATIC_ASSETS.bucketSales400,
              },
              {
                title: "RV Service",
                desc: "Roof inspections, winterization, A/C service, pre-trip inspections and more. Over 50 years combined experience!",
                href: "/rv-service",
                icon: Wrench,
                img: STATIC_ASSETS.bucketService400,
              },
              {
                title: "RV Parts",
                desc: "Shop our parts superstore for everything you need — filters, accessories, hitches, covers, and more. In-store and online.",
                href: "/rv-parts",
                icon: Package,
                img: STATIC_ASSETS.bucketParts400,
              },
              {
                title: "RV Rentals",
                desc: "Try before you buy or enjoy a weekend getaway. Rent one of our well-maintained RVs for your next adventure.",
                href: "/rentals",
                icon: CarFront,
                img: STATIC_ASSETS.bucketRentals400,
              },
            ].map((service) => (
              <Link
                key={service.title}
                to={service.href}
                className="group block overflow-hidden rounded-xl border bg-card shadow-xs transition hover:shadow-lg hover:border-primary/30"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={service.img}
                    alt={service.title}
                    width={400}
                    height={225}
                    className="h-full w-full object-cover transition group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                    fetchPriority={"low"}
                  />
                </div>
                <div className="p-6">
                  <service.icon className="h-8 w-8 text-primary" />
                  <h3 className="mt-3 font-heading text-xl font-bold uppercase text-card-foreground">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{service.desc}</p>
                  <span className="mt-3 inline-block font-heading text-sm font-semibold uppercase text-primary">
                    Learn about {service.title} →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      </InteractionGate>

      <LazySection
        fallback={
          <div className="py-16">
            <div className="container">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-40 rounded-xl" />
                ))}
              </div>
            </div>
          </div>
        }
        className="min-h-[1130px] md:min-h-[564px]"
      >
        <Suspense
          fallback={
            <div className="py-16 min-h-[1130px] md:min-h-[564px]">
              <div className="container">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-40 rounded-xl" />
                  ))}
                </div>
              </div>
            </div>
          }
        >
          <BrandLogos />
        </Suspense>
      </LazySection>

      <LazySection
        as="section"
        className="relative py-20 overflow-hidden bg-secondary"
        fallback={<div className="bg-secondary min-h-[1010px] sm:min-h-[940px] md:min-h-[880px]" />}
      >
        <img
          src={toWebP("/images/site/forest-bg.jpg", { w: 1920 })}
          alt=""
          width={1920}
          height={1080}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          fetchPriority={"low"}
          decoding="async"
        />
        <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" />
        <div className="container relative z-10">
          <div className="mx-auto max-w-3xl rounded-2xl bg-card/90 p-8 shadow-xl backdrop-blur-sm text-center md:p-12 ring-1 ring-border/30">
            <h2 className="font-heading text-3xl font-black uppercase text-foreground md:text-4xl">Rex & Sons RVs</h2>
            <p className="mt-1 font-heading text-xl italic text-muted-foreground md:text-2xl">
              Enjoy Life's Journey in a RV!
            </p>

            <div className="mt-6 space-y-4 text-sm leading-relaxed text-muted-foreground md:text-base">
              <p>
                Welcome to Rex & Sons RV's Inc, a family owned and operated RV dealership since 1997! Located in
                Wilmington, North Carolina, our dealership offers sales, service, rentals and a parts superstore. Our
                goal here at Rex and Son's RV is provide our customers with the ultimate camping experience starting
                with finding you the perfect fit for your RV needs.
              </p>
              <p>
                We proudly carry a large selection of Class A, Class B, Class C Motorhomes, Travel Trailers and Toy
                Haulers. Offering units from a range of industry-leading manufacturers such as East to West, Coachmen,
                InTech RV, Palomino, Venture RV and Winnebago!
              </p>
              <p>
                With our highly trained and professional team, we are focused on satisfying our customers needs. Stop by
                our location in Wilmington, NC today to check out our sales, service, parts and rental departments or
                take a look at our online parts and accessories store right here!
              </p>
            </div>

            <Link
              to="/rv-search?special=deal-of-week"
              className="mt-8 inline-flex items-center gap-4 rounded-xl border bg-muted/50 px-6 py-4 transition hover:shadow-md hover:border-primary/30 group"
            >
              <img
                src={optimizedImg("/images/site/dealoftheweek.jpg", "associated")}
                alt="Deal Of The Week"
                width={160}
                height={80}
                className="h-20 w-auto object-contain transition group-hover:scale-105"
                loading="lazy"
                decoding="async"
              />
            </Link>

            <div className="mt-8">
              <Button asChild size="lg" variant="outline" className="font-heading uppercase tracking-wide">
                <Link to="/about-us">About Rex & Sons RVs</Link>
              </Button>
            </div>
          </div>
        </div>
      </LazySection>

      <LazySection
        as="section"
        className="bg-muted py-16"
        fallback={
          <div className="bg-muted py-16 min-h-[616px] md:min-h-[362px]">
            <div className="container">
              <div className="grid gap-6 md:grid-cols-2">
                {Array.from({ length: 2 }).map((_, i) => (
                  <Skeleton key={i} className="h-32 rounded-xl" />
                ))}
              </div>
            </div>
          </div>
        }
      >
        <div className="container">
          <h2 className="text-center font-heading text-3xl font-bold uppercase text-foreground">
            Happy <span className="text-primary">Campers</span>
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {testimonials.map((t) => (
              <div key={t.author} className="rounded-xl border bg-card p-6 shadow-xs">
                <p className="italic text-muted-foreground">"{t.text}"</p>
                <p className="mt-4 font-heading text-sm font-bold uppercase text-primary">— {t.author}</p>
              </div>
            ))}
          </div>
        </div>
      </LazySection>

      <LazySection as="section" className="bg-primary py-16" fallback={<div className="bg-primary py-16 min-h-[280px]" />}>
        <div className="container text-center">
          <h2 className="font-heading text-3xl font-bold uppercase text-primary-foreground md:text-4xl">
            Ready to Hit the Road?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-primary-foreground/90">
            Call us today or visit our dealership in Castle Hayne, NC.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" variant="secondary" className="font-heading uppercase tracking-wide">
              <a href="tel:910-793-2894">
                <Phone className="mr-2 h-4 w-4" />
                Call 910-793-2894
              </a>
            </Button>
            <Button asChild size="lg" variant="secondary" className="font-heading uppercase tracking-wide">
              <Link to="/contact-us">Contact Us</Link>
            </Button>
          </div>
        </div>
      </LazySection>

      <InteractionGate placeholder={null}>
        <Suspense fallback={null}>
          <WidgetZone zone="home_page_bottom" />
        </Suspense>
      </InteractionGate>
    </Layout>
  );
};

export default Index;
